import React, { useState, useEffect, useRef } from 'react';
import { Modal, Box, Typography, Button, Snackbar, Alert } from '@mui/material';
import { API_ENDPOINTS } from './endpoints';
import { postData } from './axiosInstance';

const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
};

const arrayBufferEqual = (buf1, buf2) => {
    if (buf1.byteLength !== buf2.byteLength) return false;
    const dv1 = new Uint8Array(buf1);
    const dv2 = new Uint8Array(buf2);
    for (let i = 0; i < buf1.byteLength; i++) {
        if (dv1[i] !== dv2[i]) return false;
    }
    return true;
};

const PushNotificationManager = () => {
    const [showPrompt, setShowPrompt] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const initialized = useRef(false);

    const [currentIsSubscribed, setCurrentIsSubscribed] = useState(() => {
        const raw = localStorage.getItem('userData');
        const parsed = raw ? JSON.parse(raw) : null;
        return parsed?.is_subscribed === true;
    });

    useEffect(() => {
        const raw = localStorage.getItem('userData');
        const userData = raw ? JSON.parse(raw) : null;
        const userId = userData?.id;

        if (!userId || initialized.current) return;
        initialized.current = true;

        if (!('Notification' in window) || !('serviceWorker' in navigator)) return;

        const permission = Notification.permission;
        if (permission !== 'granted' || !currentIsSubscribed) {
            setShowPrompt(true);
        }
    }, [currentIsSubscribed]);

    const handlePushSubscription = async () => {
        setSnackbar({ open: true, message: 'Starting subscription...', severity: 'info' });

        try {
            const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;

            if (!vapidKey) {
                throw new Error('VAPID key not found in environment variables. Please check your .env file.');
            }

            if (!('serviceWorker' in navigator)) {
                throw new Error('Service Workers are not supported in this browser.');
            }

            let swRegistration = null;

            try {
                swRegistration = await navigator.serviceWorker.ready;
            } catch (error) {
                try {
                    swRegistration = await navigator.serviceWorker.register('/service-worker.js');
                } catch (regError) {
                    throw new Error(`Failed to register Service Worker: ${regError.message}`);
                }
            }

            let subscription = await swRegistration.pushManager.getSubscription();

            if (subscription) {
                const currentKey = subscription.options.applicationServerKey;
                const newKey = urlBase64ToUint8Array(vapidKey);
                if (!currentKey || !arrayBufferEqual(currentKey, newKey)) {
                    await subscription.unsubscribe();
                    subscription = null;
                }
            }

            if (!subscription) {
                subscription = await swRegistration.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: urlBase64ToUint8Array(vapidKey),
                });
            }

            const raw = localStorage.getItem('userData');
            const userData = raw ? JSON.parse(raw) : null;
            const accessToken = localStorage.getItem('authToken');

            const payload = {
                user_id: userData?.id,
                subscription: subscription.toJSON(),
            };

            const finalUrl = typeof API_ENDPOINTS.savePushSubscription === 'function'
                ? API_ENDPOINTS.savePushSubscription()
                : API_ENDPOINTS.savePushSubscription;

            await postData(finalUrl, payload, {
                headers: { 'Authorization': `Bearer ${accessToken}` }
            });

            // Update localStorage on success
            const updatedUserData = { ...userData, is_subscribed: true };
            localStorage.setItem('userData', JSON.stringify(updatedUserData));

            setCurrentIsSubscribed(true);
            setShowPrompt(false);
            setSnackbar({ open: true, message: 'Notifications enabled!', severity: 'success' });

        } catch (error) {
            setSnackbar({ open: true, message: `Error: ${error.message}`, severity: 'error' });
        }
    };

    const requestNotificationPermission = async () => {
        try {
            const permission = await Notification.requestPermission();
            if (permission === 'granted') {
                await handlePushSubscription();
            } else {
                setShowPrompt(false);
                setSnackbar({ open: true, message: 'Permission denied.', severity: 'warning' });
            }
        } catch (err) {
            setShowPrompt(false);
        }
    };

    return (
        <>
            <Modal open={showPrompt} onClose={() => setShowPrompt(false)}>
                <Box sx={{
                    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                    width: { xs: '90%', md: '450px' }, bgcolor: 'background.paper', p: 4, borderRadius: 2, outline: 'none'
                }}>
                    <Typography variant="h6" fontWeight="600" gutterBottom>Enable Notifications</Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        Would you like to receive real-time updates and announcements?
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button variant="outlined" sx={{ textTransform: 'none' }} onClick={() => setShowPrompt(false)}>Later</Button>
                        <Button variant="contained" sx={{ textTransform: 'none' }} onClick={requestNotificationPermission}>Enable</Button>
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} sx={{ width: '100%', minWidth: '300px' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default PushNotificationManager;